import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAuthenticatedClient, getCurrentUserId } from '@/services';
import { toast } from 'sonner';

// ── Types ─────────────────────────────────────────────

interface AssignedFarmer {
  id: string;
  full_name: string | null;
  region: string | null;
  village: string | null;
  avatar_url: string | null;
  phone: string | null;
  latest_score: number | null;
  last_visit_date: string | null;
  active_batch_status: string | null;
}

interface FarmVisit {
  id: string;
  supervisor_id: string;
  farmer_id: string;
  visit_type: string;
  status: string;
  scheduled_date: string;
  completed_at: string | null;
  notes: string | null;
  findings: Record<string, unknown>;
  follow_up_required: boolean;
  follow_up_date: string | null;
  farmer?: { full_name: string | null; region: string | null } | null;
}

const SUPERVISOR_ROLES = ['field_officer', 'manager', 'admin', 'owner', 'integrator_admin'] as const;

// ── Supervisor role check ─────────────────────────────

export function useSupervisorRole() {
  const userId = getCurrentUserId();
  const client = getAuthenticatedClient();

  return useQuery({
    queryKey: ['supervisor-role', userId],
    queryFn: async (): Promise<boolean> => {
      if (!userId) return false;
      const { data, error } = await client
        .from('organization_members')
        .select('member_role')
        .eq('user_id', userId)
        .in('member_role', SUPERVISOR_ROLES)
        .limit(1);
      if (error) return false;
      return (data?.length ?? 0) > 0;
    },
    staleTime: 1000 * 60 * 5,
  });
}

// ── Assigned farmers ──────────────────────────────────

export function useAssignedFarmers(supervisorId?: string | null) {
  const client = getAuthenticatedClient();
  const userId = supervisorId || getCurrentUserId();

  return useQuery({
    queryKey: ['supervisor-farmers', userId],
    queryFn: async (): Promise<AssignedFarmer[]> => {
      if (!userId) return [];

      const { data: assignments, error: aErr } = await client
        .from('supervisor_assignments')
        .select('farmer_id')
        .eq('supervisor_id', userId)
        .eq('is_active', true);

      if (aErr || !assignments?.length) return [];

      const farmerIds = assignments.map((a: { farmer_id: string }) => a.farmer_id);

      const { data: farmers, error: fErr } = await client
        .from('profiles')
        .select('id, full_name, region, village, avatar_url, phone')
        .in('id', farmerIds);

      if (fErr || !farmers) return [];

      // Get latest credit scores
      const { data: scores } = await client
        .from('credit_scores')
        .select('user_id, total_score')
        .in('user_id', farmerIds)
        .order('created_at', { ascending: false });

      const scoreMap = new Map<string, number>();
      for (const s of scores || []) {
        if (!scoreMap.has(s.user_id)) scoreMap.set(s.user_id, s.total_score);
      }

      // Get last visit dates
      const { data: visits } = await client
        .from('farm_visits')
        .select('farmer_id, scheduled_date, status')
        .in('farmer_id', farmerIds)
        .eq('status', 'completed')
        .order('scheduled_date', { ascending: false });

      const visitMap = new Map<string, string>();
      for (const v of visits || []) {
        if (!visitMap.has(v.farmer_id)) visitMap.set(v.farmer_id, v.scheduled_date);
      }

      // Get active batch status
      const { data: batches } = await client
        .from('batches')
        .select('user_id, batch_status')
        .in('user_id', farmerIds)
        .in('batch_status', ['active', 'placed']);

      const batchMap = new Map<string, string>();
      for (const b of batches || []) {
        batchMap.set(b.user_id, b.batch_status);
      }

      return farmers.map((f: { id: string; full_name: string | null; region: string | null; village: string | null; avatar_url: string | null; phone: string | null }) => ({
        id: f.id,
        full_name: f.full_name,
        region: f.region,
        village: f.village,
        avatar_url: f.avatar_url,
        phone: f.phone,
        latest_score: scoreMap.get(f.id) ?? null,
        last_visit_date: visitMap.get(f.id) ?? null,
        active_batch_status: batchMap.get(f.id) ?? null,
      }));
    },
    enabled: !!userId,
  });
}

// ── Visit schedule (upcoming N days) ──────────────────

export function useVisitSchedule(supervisorId?: string | null, days = 7) {
  const client = getAuthenticatedClient();
  const userId = supervisorId || getCurrentUserId();

  return useQuery({
    queryKey: ['supervisor-visits-schedule', userId, days],
    queryFn: async (): Promise<FarmVisit[]> => {
      if (!userId) return [];

      const today = new Date().toISOString().split('T')[0];
      const futureDate = new Date(Date.now() + days * 86400000).toISOString().split('T')[0];

      const { data, error } = await client
        .from('farm_visits')
        .select(`
          id, supervisor_id, farmer_id, visit_type, status,
          scheduled_date, completed_at, notes, findings,
          follow_up_required, follow_up_date,
          farmer:profiles!farm_visits_farmer_id_fkey(full_name, region)
        `)
        .eq('supervisor_id', userId)
        .in('status', ['scheduled', 'overdue'])
        .gte('scheduled_date', today)
        .lte('scheduled_date', futureDate)
        .order('scheduled_date', { ascending: true });

      if (error) throw error;

      return (data || []).map((v: Record<string, unknown>) => ({
        ...v,
        farmer: Array.isArray(v.farmer) ? v.farmer[0] : v.farmer,
      })) as FarmVisit[];
    },
    enabled: !!userId,
  });
}

// ── Visit history for a farmer ────────────────────────

export function useVisitHistory(farmerId: string) {
  const client = getAuthenticatedClient();

  return useQuery({
    queryKey: ['supervisor-visit-history', farmerId],
    queryFn: async (): Promise<FarmVisit[]> => {
      const { data, error } = await client
        .from('farm_visits')
        .select(`
          id, supervisor_id, farmer_id, visit_type, status,
          scheduled_date, completed_at, notes, findings,
          follow_up_required, follow_up_date
        `)
        .eq('farmer_id', farmerId)
        .order('scheduled_date', { ascending: false });

      if (error) throw error;
      return (data || []) as FarmVisit[];
    },
    enabled: !!farmerId,
  });
}

// ── Schedule a visit ──────────────────────────────────

export function useScheduleVisit() {
  const queryClient = useQueryClient();
  const client = getAuthenticatedClient();
  const userId = getCurrentUserId();

  return useMutation({
    mutationFn: async (data: {
      farmerId: string;
      visitType: string;
      scheduledDate: string;
      notes?: string;
    }) => {
      if (!userId) throw new Error('Not authenticated');

      const { error } = await client.from('farm_visits').insert({
        supervisor_id: userId,
        farmer_id: data.farmerId,
        visit_type: data.visitType,
        status: 'scheduled',
        scheduled_date: data.scheduledDate,
        notes: data.notes || null,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supervisor-visits-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['supervisor-visit-history'] });
      toast.success('Visit scheduled!');
    },
    onError: () => {
      toast.error('Failed to schedule visit');
    },
  });
}

// ── Complete a visit ──────────────────────────────────

export function useCompleteVisit() {
  const queryClient = useQueryClient();
  const client = getAuthenticatedClient();

  return useMutation({
    mutationFn: async (data: {
      visitId: string;
      notes?: string;
      findings?: Record<string, unknown>;
    }) => {
      const { error } = await client
        .from('farm_visits')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          notes: data.notes,
          findings: (data.findings || {}) as unknown as import('@/integrations/supabase/types').Json,
          updated_at: new Date().toISOString(),
        })
        .eq('id', data.visitId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supervisor-visits-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['supervisor-visit-history'] });
      queryClient.invalidateQueries({ queryKey: ['supervisor-farmers'] });
      toast.success('Visit completed!');
    },
    onError: () => {
      toast.error('Failed to complete visit');
    },
  });
}

export type { AssignedFarmer, FarmVisit };
